import { callable } from '../backend/callables';
import { VaultUnreachableError } from '../vault/cloudVault';
import type { VaultBackend } from '../vault/cloudVault';
import type { AuthSession } from '../auth/firebaseAuth';
import { isExpired, refresh, resumeFromRefreshToken } from '../auth/firebaseAuth';
import { getAuth, persistedRefreshToken, setAuth } from './authSession';

/**
 * How the vault talks to the backend. Lives apart from the hook that used to
 * hold it because the store needs it too, and a store importing from a hook
 * is the wrong direction.
 */
export function backendFor(grantSecret: Uint8Array): VaultBackend {
  return {
    async call<T>(name: string, data: Record<string, unknown>): Promise<T> {
      return callable<T>(name, data, { idToken: await liveIdToken(), grantSecret });
    },
    async fetchBlob(url: string): Promise<Uint8Array> {
      // A signed URL carries its own authorisation; sending the ID token too
      // would only leak it to Storage.
      let response: Response;
      try {
        response = await fetch(url);
      } catch (cause) {
        // A CORS refusal and a dead network are one error here by design — the
        // browser withholds which. Either way the bytes are out of reach, which
        // is a different problem from a key that will not open them.
        throw new VaultUnreachableError('the vault’s storage could not be reached', { cause });
      }
      if (!response.ok) {
        throw new VaultUnreachableError(`the vault’s storage refused the read (${response.status})`);
      }
      return new Uint8Array(await response.arrayBuffer());
    },
  };
}

/** One resume serves however many reads discover the gap at once. A window of
 *  thoughts asks per chunk and the plan and catalog ask alongside it, so
 *  without this they each race a refresh and spend the rotation against each
 *  other. */
let resuming: Promise<AuthSession | null> | null = null;

/**
 * Refreshes ahead of expiry, because an expired token is refused with the same
 * message as a revoked grant and the two must not be confusable — and resumes
 * the account outright where this tab never had one.
 *
 * That second case is not hypothetical. A tab let in by a sibling skips the
 * unlock, which is the only place the account is otherwise resumed, so it holds
 * a working vault key and no token at all. Every read then threw `not signed
 * in` from here, before any request, with nothing catching it.
 */
async function liveIdToken(): Promise<string> {
  const auth = getAuth() ?? (await resumeStoredAccount());
  if (!auth) throw new Error('not signed in');
  if (!isExpired(auth)) return auth.idToken;
  const renewed: AuthSession = await refresh(auth);
  setAuth(renewed);
  return renewed.idToken;
}

/** The refresh token stored beside this browser's key, traded for a session.
 *  Null where nothing is stored or the token is refused — both mean the way
 *  back in is the connect screen, not another read. */
function resumeStoredAccount(): Promise<AuthSession | null> {
  if (resuming) return resuming;
  const attempt = (async (): Promise<AuthSession | null> => {
    const stored = persistedRefreshToken();
    if (!stored) return null;
    try {
      const resumed = await resumeFromRefreshToken(stored);
      setAuth(resumed);
      return resumed;
    } catch {
      return null;
    }
  })();
  resuming = attempt;
  void attempt.finally(() => {
    resuming = null;
  });
  return attempt;
}
